<script setup lang="ts">
import useUserStore from "~/stores/user";
import useEventStore from "~/stores/event";
import useTagFilters from "~/hooks/tags/use-tag-filters";

const { t } = useI18n();
const route = useRoute();
const userStore = useUserStore();
const eventStore = useEventStore();
const appConfig = useRuntimeConfig().public;

const emit = defineEmits(["edit"]);
const tagEdit = (tagId: string) => emit("edit", tagId);

const searchValue = ref("");
const checkedTagsIds = ref([] as string[]);
const checkedAllTags = ref(false);

const isLoggedIn = computed(() => Boolean(userStore.user));
const isTaggingModeEnabled = computed(() => appConfig.APP_CUSTOMER.web_sdk.enable_tagging_mode);
const tags = computed(() => [...eventStore.tags.data.values()]);
const error = computed(() => eventStore.tags.error);
const hasPlayerError = computed(() => eventStore.playerState?.error !== null);
const isEventStartingSoon = computed(() => eventStore.isEventStartingSoon);
const playingTime = computed(() => eventStore.playerState?.currentTime || 0);
const isPlaying = computed(() => eventStore.playerState?.playing);
const userCanCreateGlobalTags = computed(() => Boolean(userStore.user?.canCreateGlobalTag));

const createTag = () => {
  eventStore.tags.isCreatingTag = true;
  eventStore.isCreatingClip = false;
};
const setActive = (state: boolean) => eventStore.player?.userActive(state);
const pauseVideo = () => eventStore.player?.pause();
const playVideo = (tagTime: number) => {
  eventStore.player?.currentTime(tagTime);
  return eventStore.player?.play();
};

const { data: filteredTags, filters, activeFilters } = useTagFilters(tags, { searchValue });
const isTagChecked = (tag: IModelTag) => checkedTagsIds.value.includes(tag.id);
const checkTag = (tag: IModelTag) => {
  const index = checkedTagsIds.value.indexOf(tag.id);

  if (index !== -1)
    checkedTagsIds.value.splice(index, 1);
  else
    checkedTagsIds.value.push(tag.id);
};

const checkAllTags = () => {
  filteredTags.value.forEach((tag) => {
    const index = checkedTagsIds.value.indexOf(tag.id);

    if (!checkedAllTags.value && index !== -1)
      checkedTagsIds.value.splice(index, 1);
    else if (checkedAllTags.value && !checkedTagsIds.value.includes(tag.id))
      checkedTagsIds.value.push(tag.id);
  });
};

const updateTagsValue = () => {
  checkedTagsIds.value = [];
  checkedAllTags.value = false;
};

watch(checkedAllTags, checkAllTags);
watch(eventStore.tags.data, updateTagsValue);
</script>

<template>
  <template v-if="error">
    <BaseNotFound :text="error" />
  </template>
  <template v-else-if="tags.length">
    <div class="h-full max-lg:max-h-[49vh] flex flex-col">
      <EventTagsHead
        v-model:searchValue="searchValue"
        v-model:active-filters="activeFilters"
        v-model:is-all-tags-selected="checkedAllTags"
        :filters="filters"
        :checked-tags-total="checkedTagsIds.length"
      />
      <div v-if="!filteredTags.length" class="text-secondary text-center py-8">
        {{ t("pages.events.tabs.event_tags.filters.no_tags") }}
      </div>
      <ul v-auto-animate class="max-lg:max-h-[calc(49vh-60px)] h-[calc(100%-60px)] overflow-auto pb-24">
        <li v-for="tag in filteredTags" :key="tag.id" class="py-1">
          <EventTag
            :id="tag.id"
            :title="tag.title"
            :is-playing="isPlaying"
            :playing-time="playingTime"
            :type="tag.type"
            :team-name="tag.tagTeam?.title"
            :has-menu="true"
            :has-menu-disabled="tag.global && !userCanCreateGlobalTags"
            :player-number="tag.playerNumber"
            :team-color="tag.tagTeam?.color"
            :start-time="tag.startTime"
            :end-time="tag.endTime"
            @edit="tagEdit"
            @stop="pauseVideo()"
            @play="playVideo"
            @mouseover="setActive(true)"
          >
            <template #start>
              <BaseCheckbox
                name="check-tag"
                class="text-secondary"
                :model-value="isTagChecked(tag)"
                @input="checkTag(tag)"
              />
            </template>
          </EventTag>
        </li>
      </ul>
      <!-- Will be implemented in future. -->

      <EventTagsFooter :tags-ids="checkedTagsIds" :can-show-action-button="Boolean(checkedTagsIds.length)" />
    </div>
  </template>
  <template v-else>
    <div class="w-full text-center py-10 px-4 flex-col items-center flex">
      <IconTag fill="var(--base-icon-color)" :width="32" :height="32" />
      <template v-if="!isTaggingModeEnabled">
        <p class="text-body-medium text-secondary py-6">
          {{ t("pages.events.tabs.event_tags.title_enabled_tagging") }}
        </p>
      </template>
      <template v-else-if="isLoggedIn">
        <p class="text-body-medium text-secondary py-6">
          {{ t("pages.events.tabs.event_tags.title_no_tags") }}
        </p>
        <BaseButton name="createTag" class="mb-5" type="primary" :disabled="hasPlayerError || isEventStartingSoon" @click="createTag">
          <div class="flex items-center gap-2">
            <IconTag :width="16" :height="16" fill="#ffff" />
            <span class="text-button-bold ">{{ t("pages.events.tabs.event_tags.create_tag") }}</span>
          </div>
        </BaseButton>
      </template>
      <template v-else>
        <p class="text-body-medium text-secondary py-6">
          {{ t("pages.events.tabs.event_tags.title_no_tags_not_logged_in") }}
        </p>
        <BaseButton
          name="sign-in"
          tag="nuxt-link"
          :title="t('labels.sign_in')"
          :to="{ path: '/auth/login/', query: { redirectUrl: route.fullPath } }"
          class="font-bold text-white"
        >
          {{ t("labels.sign_in") }}
        </BaseButton>
      </template>
    </div>
  </template>
</template>
